import React, { useEffect, useState } from "react";
import { Box, MenuItem, Select, TextField } from "@mui/material";
import { Link } from "react-router-dom";
import axios from "axios";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import {
  useEngagementTheme,
  engagementPageSx,
  EngagementCard,
  EngagementPill,
  engagementButtonSx,
} from "../components/engagementTheme";

const CATEGORY_NAMES = ["All tasks", "Renewal", "Calls", "Training", "Feedback", "Camp", "Influencer", "Upgrade", "Onboarding", "Support"];
const CATEGORY_TONE = { Renewal: "crit", Calls: "info", Influencer: "warn", Camp: "warn", Training: "ok", Feedback: "info", Upgrade: "mute", Onboarding: "ok", Support: "mute" };
const CALL_OUTCOMES = ["Spoke", "Busy", "Did not answer", "Wrong number", "Call back later"];
const BUCKET_LABEL = { overdue: "Overdue", today: "Today", week: "Later this week" };
const BUCKET_TONE = { overdue: "crit", today: "ok", week: "mute" };

const TaskRow = ({ task, officers, onLogged }) => {
  const t = useEngagementTheme();
  const [showDone, setShowDone] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [outcome, setOutcome] = useState(["Calls", "Renewal", "Feedback"].includes(task.category) ? "Spoke" : "Done");
  const [notes, setNotes] = useState("");
  const [moveTo, setMoveTo] = useState("");
  const [moveNote, setMoveNote] = useState("");
  const [saving, setSaving] = useState(false);
  const Toast = useToast();
  const auth = localStorage.getItem("token");
  const authHeaders = { headers: { Authorization: `Bearer ${auth}` } };
  const needsOutcome = ["Calls", "Renewal", "Feedback"].includes(task.category);

  const complete = async () => {
    setSaving(true);
    try {
      await axios.patch(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/my-day/tasks/${task.id}/complete`, { outcome, notes }, authHeaders);
      Toast.success(`${task.action} closed for ${task.doctor}. Logged to the clinic timeline.`);
      onLogged();
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not close this task.");
    } finally {
      setSaving(false);
    }
  };

  const move = async () => {
    if (!moveTo) return;
    setSaving(true);
    try {
      await axios.patch(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/my-day/tasks/${task.id}/reassign`, { newOwnerId: moveTo, note: moveNote }, authHeaders);
      Toast.success(`${task.action} moved. It's in their My Day now.`);
      onLogged();
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not move this task.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ bgcolor: t.cardBg, padding: "14px 18px", display: "flex", flexDirection: "column", gap: "12px" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px" }}>
        <EngagementPill t={t} tone={CATEGORY_TONE[task.category] || "mute"} sx={{ width: "120px", justifyContent: "center" }}>{task.category}</EngagementPill>
        <Box sx={{ flex: "1 1 300px", minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
          <Box component="span" sx={{ fontSize: 16, fontWeight: 700 }}>{task.action}</Box>
          <Box component="span" sx={{ fontSize: 14, color: t.textSecondary }}>
            <Box component={Link} to={`/dashboard/engagement/clinics/${task.doctorId}`} sx={{ fontWeight: 600, textDecoration: "none", color: t.accent }}>{task.doctor}</Box>
            {`, ${task.clinic}, ${task.city}. `}
            {task.phone && <Box component="a" href={`tel:${task.phone}`} sx={{ textDecoration: "none", color: t.accent }}>{task.phone}</Box>}
          </Box>
          {task.note && <Box component="span" sx={{ fontSize: 13, color: t.textMuted }}>{task.note}</Box>}
        </Box>
        <EngagementPill t={t} tone={task.source === "auto" ? "mute" : task.source === "reassigned" ? "warn" : "info"}>{task.sourceLabel}</EngagementPill>
        <EngagementPill t={t} tone={BUCKET_TONE[task.dueBucket]}>{BUCKET_LABEL[task.dueBucket]}</EngagementPill>
        <Box sx={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
          <Box component="button" type="button" onClick={() => { setShowDone((o) => !o); setShowMove(false); }} sx={engagementButtonSx(t)}>Complete</Box>
          <Box component="button" type="button" onClick={() => { setShowMove((o) => !o); setShowDone(false); }} sx={{ ...engagementButtonSx(t), border: `1px solid ${t.disabledBorder}`, color: t.textPrimary }}>Reassign</Box>
        </Box>
      </Box>

      {showDone && (
        <Box sx={{ padding: "14px", borderRadius: "10px", bgcolor: t.hoverBg, display: "flex", flexDirection: "column", gap: "12px" }}>
          {needsOutcome && (
            <Box role="group" aria-label="Outcome" sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {CALL_OUTCOMES.map((o) => (
                <Box
                  key={o}
                  component="button"
                  type="button"
                  onClick={() => setOutcome(o)}
                  sx={{
                    minHeight: 44, padding: "0 14px", border: "1px solid", borderColor: outcome === o ? t.selectedBg : t.disabledBorder,
                    borderRadius: "8px", bgcolor: outcome === o ? t.selectedBg : t.cardBg, color: outcome === o ? t.selectedText : t.textPrimary,
                    font: "inherit", fontSize: 14, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  {o}
                </Box>
              ))}
            </Box>
          )}
          <TextField label="What was discussed, and action items" multiline minRows={2} value={notes} onChange={(e) => setNotes(e.target.value)} sx={{ bgcolor: t.inputBg }} />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
            <Box component="button" type="button" disabled={saving} onClick={complete} sx={{ ...engagementButtonSx(t, "solid"), opacity: saving ? 0.7 : 1 }}>
              {saving ? "Saving…" : "Save and close task"}
            </Box>
            <Box component="span" sx={{ fontSize: 13, color: t.textMuted }}>Saved to the clinic's timeline and the master database.</Box>
          </Box>
        </Box>
      )}

      {showMove && (
        <Box sx={{ padding: "14px", borderRadius: "10px", bgcolor: t.hoverBg, display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-end" }}>
          <Select size="small" displayEmpty value={moveTo} onChange={(e) => setMoveTo(e.target.value)} sx={{ height: 44, minWidth: 180, bgcolor: t.inputBg }}>
            <MenuItem value="" disabled>Move to</MenuItem>
            {officers.filter((o) => o.id !== task.assignedToId).map((o) => (
              <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
            ))}
          </Select>
          <TextField placeholder="Why you are passing it on" value={moveNote} onChange={(e) => setMoveNote(e.target.value)} sx={{ bgcolor: t.inputBg, flex: "1 1 260px" }} />
          <Box component="button" type="button" disabled={!moveTo || saving} onClick={move} sx={{ ...engagementButtonSx(t), opacity: !moveTo || saving ? 0.6 : 1 }}>Move task</Box>
        </Box>
      )}
    </Box>
  );
};

const todayLabel = () => new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

const BUCKET_KEYS = ["overdue", "today", "week"];

const EngagementMyDay = () => {
  const t = useEngagementTheme();
  const [data, setData] = useState({ categoryCounts: {}, buckets: {}, given: [], officers: [], installs: [] });
  const [tab, setTab] = useState("My tasks");
  const [category, setCategory] = useState("All tasks");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [givenPage, setGivenPage] = useState(1);
  const [overduePage, setOverduePage] = useState(1);
  const [todayPage, setTodayPage] = useState(1);
  const [weekPage, setWeekPage] = useState(1);

  const [assignOpen, setAssignOpen] = useState(false);
  const [clinicQuery, setClinicQuery] = useState("");
  const [clinicOptions, setClinicOptions] = useState([]);
  const [clinic, setClinic] = useState(null);
  const [assignCategory, setAssignCategory] = useState("Calls");
  const [assignTo, setAssignTo] = useState("me");
  const [assignDue, setAssignDue] = useState("");
  const [assignWhat, setAssignWhat] = useState("");
  const [saving, setSaving] = useState(false);

  const Toast = useToast();
  const auth = localStorage.getItem("token");
  const authHeaders = { headers: { Authorization: `Bearer ${auth}` } };

  const load = async (keyword) => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/my-day`, {
        ...authHeaders,
        params: { q: keyword || undefined, givenPage, category, overduePage, todayPage, weekPage },
      });
      setData(res.data.data);
    } catch (error) {
      Toast.error("Error while fetching My Day");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => load(search), search ? 400 : 0);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, refreshKey, givenPage, category, overduePage, todayPage, weekPage]);

  const pickCategory = (label) => {
    setCategory(label);
    setOverduePage(1);
    setTodayPage(1);
    setWeekPage(1);
  };

  const searchClinics = async (keyword) => {
    setClinicQuery(keyword);
    setClinic(null);
    if (!keyword || keyword.trim().length < 2) {
      setClinicOptions([]);
      return;
    }
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/clinics`, { ...authHeaders, params: { q: keyword, limit: 8 } });
      setClinicOptions(res.data.rows || []);
    } catch {
      setClinicOptions([]);
    }
  };

  const assignTask = async () => {
    if (!clinic || !assignWhat.trim()) return;
    setSaving(true);
    try {
      const assignToId = assignTo === "me" ? undefined : assignTo;
      await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/my-day/tasks`,
        { doctorId: clinic.id, category: assignCategory, action: assignWhat.trim(), dueDate: assignDue || undefined, assignToId },
        authHeaders
      );
      Toast.success(assignTo === "me" ? "Task added to your list." : "Assigned. It shows in their My Day.");
      setAssignOpen(false);
      setClinic(null);
      setClinicQuery("");
      setAssignWhat("");
      setAssignDue("");
      setAssignCategory("Calls");
      setAssignTo("me");
      load(search);
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not create this task.");
    } finally {
      setSaving(false);
    }
  };

  const onLogged = () => {
    setRefreshKey((k) => k + 1);
  };

  const categoryCounts = data.categoryCounts || {};
  const mineTotalCount = data.mineTotalCount || 0;
  const mineOverdueCount = data.mineOverdueCount || 0;
  const cats = CATEGORY_NAMES.map((n) => ({ label: n, count: n === "All tasks" ? mineTotalCount : categoryCounts[n] || 0 })).filter(
    (c) => c.label === "All tasks" || c.count > 0
  );

  const buckets = data.buckets || {};
  const bucketPage = { overdue: overduePage, today: todayPage, week: weekPage };
  const setBucketPage = { overdue: setOverduePage, today: setTodayPage, week: setWeekPage };
  const groups = BUCKET_KEYS.map((key) => ({ key, title: BUCKET_LABEL[key], tone: BUCKET_TONE[key], ...(buckets[key] || { rows: [], totalCount: 0, totalPages: 1 }) })).filter(
    (g) => g.totalCount > 0
  );

  return (
    <Box sx={engagementPageSx(t)}>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-end", gap: "16px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Box component="h1" sx={{ margin: 0, fontSize: 30, fontWeight: 700, letterSpacing: "-0.015em" }}>My Day</Box>
          <Box component="p" sx={{ margin: 0, fontSize: 15, color: t.textMuted }}>
            {todayLabel()}. {mineTotalCount} tasks against your daily cap of {data.dailyTaskCap || 25}, {mineOverdueCount} overdue, sorted by renewal first.
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-end" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Box component="label" htmlFor="md-q" sx={{ fontSize: 13, color: t.textMuted }}>Search a doctor</Box>
            <TextField
              id="md-q"
              size="small"
              placeholder="Doctor or clinic name"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setOverduePage(1); setTodayPage(1); setWeekPage(1); }}
              sx={{ width: 260, maxWidth: "100%", bgcolor: t.inputBg, "& .MuiOutlinedInput-root": { height: 44 } }}
            />
          </Box>
          <Box component="button" type="button" onClick={() => setAssignOpen((o) => !o)} sx={engagementButtonSx(t, "solid")}>
            {assignOpen ? "Close" : "Assign a task"}
          </Box>
        </Box>
      </Box>

      {assignOpen && (
        <EngagementCard t={t} sx={{ border: `2px solid ${t.accent}` }}>
          <Box component="h2" sx={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Assign a task</Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "flex-end" }}>
            <Box sx={{ flex: "1 1 260px", display: "flex", flexDirection: "column", gap: "4px" }}>
              <TextField label="Doctor and clinic" value={clinicQuery} onChange={(e) => searchClinics(e.target.value)} sx={{ bgcolor: t.inputBg }} />
              {!clinic && clinicOptions.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  {clinicOptions.map((o) => (
                    <Box key={o.id} onClick={() => { setClinic(o); setClinicQuery(`${o.doctorName}, ${o.clinicName}`); setClinicOptions([]); }} sx={{ cursor: "pointer", padding: "6px 10px", borderRadius: "8px", bgcolor: t.hoverBg, fontSize: 14 }}>
                      {o.doctorName}, {o.clinicName}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Box component="label" sx={{ fontSize: 13, color: t.textMuted }}>Type</Box>
              <Select size="small" value={assignCategory} onChange={(e) => setAssignCategory(e.target.value)} sx={{ height: 44, minWidth: 150, bgcolor: t.inputBg }}>
                {CATEGORY_NAMES.filter((c) => c !== "All tasks").map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Box component="label" sx={{ fontSize: 13, color: t.textMuted }}>Assign to</Box>
              <Select size="small" value={assignTo} onChange={(e) => setAssignTo(e.target.value)} sx={{ height: 44, minWidth: 150, bgcolor: t.inputBg }}>
                <MenuItem value="me">Me</MenuItem>
                {(data.officers || []).map((o) => (
                  <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                ))}
              </Select>
            </Box>
            <TextField label="Due" type="date" InputLabelProps={{ shrink: true }} value={assignDue} onChange={(e) => setAssignDue(e.target.value)} sx={{ bgcolor: t.inputBg }} />
          </Box>
          <TextField label="What needs doing, with any context" value={assignWhat} onChange={(e) => setAssignWhat(e.target.value)} sx={{ bgcolor: t.inputBg }} />
          <Box sx={{ display: "flex", gap: "10px" }}>
            <Box component="button" type="button" disabled={saving || !clinic || !assignWhat.trim()} onClick={assignTask} sx={{ ...engagementButtonSx(t, "solid"), opacity: saving || !clinic || !assignWhat.trim() ? 0.6 : 1 }}>
              {saving ? "Saving…" : "Assign"}
            </Box>
            <Box component="button" type="button" onClick={() => setAssignOpen(false)} sx={{ ...engagementButtonSx(t), border: 0 }}>Cancel</Box>
          </Box>
        </EngagementCard>
      )}

      <Box role="tablist" aria-label="Task lists" sx={{ display: "flex", gap: "4px", borderBottom: `1px solid ${t.border}` }}>
        {["My tasks", "Assigned by me"].map((tabName) => (
          <Box
            key={tabName}
            component="button"
            type="button"
            role="tab"
            aria-selected={tab === tabName}
            onClick={() => setTab(tabName)}
            sx={{
              minHeight: 48, padding: "0 14px", border: 0, borderBottom: "3px solid", borderBottomColor: tab === tabName ? t.accent : "transparent",
              bgcolor: "transparent", font: "inherit", fontSize: 15, fontWeight: 600, cursor: "pointer", color: tab === tabName ? t.textPrimary : t.textMuted,
            }}
          >
            {tabName}
          </Box>
        ))}
      </Box>

      {tab === "My tasks" && (
        <>
          <Box role="group" aria-label="Task type" sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(132px, 1fr))", gap: "10px" }}>
            {cats.map((c) => (
              <Box
                key={c.label}
                component="button"
                type="button"
                aria-pressed={category === c.label}
                onClick={() => pickCategory(c.label)}
                sx={{
                  minHeight: 72,
                  padding: "10px 14px",
                  border: "1px solid",
                  borderColor: category === c.label ? t.selectedBg : t.disabledBorder,
                  borderRadius: "12px",
                  font: "inherit",
                  textAlign: "left",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                  bgcolor: category === c.label ? t.selectedBg : t.cardBg,
                  color: category === c.label ? t.selectedText : t.textPrimary,
                }}
              >
                <Box component="span" sx={{ fontSize: 24, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{c.count}</Box>
                <Box component="span" sx={{ fontSize: 14, fontWeight: 600 }}>{c.label}</Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "20px", alignItems: "flex-start" }}>
            <Box sx={{ flex: "999 1 640px", minWidth: 0, display: "flex", flexDirection: "column", gap: "18px", position: "relative" }}>
              {loading && <PageLoader />}
              {groups.map((g) => (
                <Box key={g.key} sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
                    <Box component="h2" sx={{ margin: 0, fontSize: 18, fontWeight: 700, color: g.tone === "ok" ? t.tone.ok.color : t.txtTone[g.tone] }}>{g.title}</Box>
                    <Box component="span" sx={{ fontSize: 14, color: t.textMuted }}>{g.totalCount} task{g.totalCount === 1 ? "" : "s"}</Box>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "1px", bgcolor: t.border, border: `1px solid ${t.border}`, borderRadius: "12px", overflow: "hidden" }}>
                    {g.rows.map((r) => (
                      <TaskRow key={r.id} task={r} officers={data.officers || []} onLogged={onLogged} />
                    ))}
                  </Box>
                  {g.totalPages > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
                      <Box component="span" sx={{ fontSize: 13, color: t.textMuted }}>Page {bucketPage[g.key]} of {g.totalPages}</Box>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Box
                          component="button"
                          type="button"
                          disabled={bucketPage[g.key] <= 1}
                          onClick={() => setBucketPage[g.key]((p) => Math.max(1, p - 1))}
                          sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: bucketPage[g.key] <= 1 ? "not-allowed" : "pointer" }}
                        >
                          Previous
                        </Box>
                        <Box
                          component="button"
                          type="button"
                          disabled={bucketPage[g.key] >= g.totalPages}
                          onClick={() => setBucketPage[g.key]((p) => Math.min(g.totalPages, p + 1))}
                          sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: bucketPage[g.key] >= g.totalPages ? "not-allowed" : "pointer" }}
                        >
                          Next
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
              {groups.length === 0 && !loading && (
                <Box sx={{ bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "14px", padding: "20px", fontSize: 15, color: t.textMuted }}>
                  No open tasks match. Clear the search or pick another type.
                </Box>
              )}
            </Box>

            <Box component="aside" aria-label="Today" sx={{ flex: "1 1 300px", minWidth: 0, display: "flex", flexDirection: "column", gap: "16px" }}>
              <EngagementCard t={t}>
                <Box component="h2" sx={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Today so far</Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 15 }}><span>Tasks closed</span><strong>{data.closedTodayCount || 0}</strong></Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 15 }}><span>Still open</span><strong>{mineTotalCount}</strong></Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 15 }}><span>Overdue</span><strong style={{ color: t.txtTone.crit }}>{mineOverdueCount}</strong></Box>
                <Box component="span" sx={{ fontSize: 13, color: t.textMuted }}>
                  Auto tasks are synced from real renewal, contact, camp, feedback, support and onboarding signals each time you open this page, and close themselves once the condition clears.
                </Box>
              </EngagementCard>
              <EngagementCard t={t}>
                <Box component="h2" sx={{ margin: 0, fontSize: 17, fontWeight: 700 }}>New installations</Box>
                <Box component="span" sx={{ fontSize: 13, color: t.textMuted }}>Shows up automatically from each clinic's install date and onboarding status.</Box>
                {(data.installs || []).length === 0 && <Box component="span" sx={{ fontSize: 14, color: t.textMuted }}>None in the last 10 days.</Box>}
                {(data.installs || []).map((n, i) => (
                  <Box key={i} sx={{ display: "flex", flexDirection: "column", gap: "3px", pt: "10px", borderTop: `1px solid ${t.borderMuted}` }}>
                    <Box component="span" sx={{ fontSize: 15, fontWeight: 600 }}>{n.doctor}</Box>
                    <Box component="span" sx={{ fontSize: 13, color: t.textSecondary }}>{n.clinic}</Box>
                    <Box component="span" sx={{ fontSize: 13, color: t.textMuted }}>{n.when}</Box>
                  </Box>
                ))}
              </EngagementCard>
            </Box>
          </Box>
        </>
      )}

      {tab === "Assigned by me" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1px", bgcolor: t.border, border: `1px solid ${t.border}`, borderRadius: "12px", overflow: "hidden" }}>
          {(data.given || []).length === 0 && (
            <Box sx={{ bgcolor: t.cardBg, padding: "18px", fontSize: 14, color: t.textMuted }}>Nothing assigned to others yet.</Box>
          )}
          {(data.given || []).map((a) => (
            <Box key={a.id} sx={{ bgcolor: t.cardBg, padding: "14px 18px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px" }}>
              <EngagementPill t={t} tone="mute" sx={{ width: "120px", justifyContent: "center" }}>{a.category}</EngagementPill>
              <Box sx={{ flex: "1 1 300px", minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                <Box component="span" sx={{ fontSize: 16, fontWeight: 700 }}>{a.action}</Box>
                <Box component="span" sx={{ fontSize: 14, color: t.textSecondary }}>{a.doctor}. To {a.to}.</Box>
              </Box>
              <EngagementPill t={t} tone={a.tone}>{a.status}</EngagementPill>
            </Box>
          ))}
          {(data.givenTotalPages || 1) > 1 && (
            <Box sx={{ bgcolor: t.cardBg, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
              <Box component="span" sx={{ fontSize: 13, color: t.textMuted }}>Page {data.givenPage || 1} of {data.givenTotalPages || 1}</Box>
              <Box sx={{ display: "flex", gap: "8px" }}>
                <Box
                  component="button"
                  type="button"
                  disabled={givenPage <= 1}
                  onClick={() => setGivenPage((p) => Math.max(1, p - 1))}
                  sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: givenPage <= 1 ? "not-allowed" : "pointer" }}
                >
                  Previous
                </Box>
                <Box
                  component="button"
                  type="button"
                  disabled={givenPage >= (data.givenTotalPages || 1)}
                  onClick={() => setGivenPage((p) => Math.min(data.givenTotalPages || 1, p + 1))}
                  sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: givenPage >= (data.givenTotalPages || 1) ? "not-allowed" : "pointer" }}
                >
                  Next
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EngagementMyDay;
